import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { signup, getAppConfig, AppConfig } from '../services/api';
import { UserPlus, Phone, User, Mail, ArrowRight, Shield, CheckSquare, Square, Gift } from 'lucide-react';

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    phoneNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    referralCode: ''
  });
  const [agreedToSMS, setAgreedToSMS] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);

  // Load config and check for referral code in URL
  useEffect(() => {
    getAppConfig().then(setAppConfig).catch(() => {});

    // Check for referral code in URL (e.g., ?ref=D5B6F153)
    const refCode = searchParams.get('ref') || searchParams.get('referral');
    if (refCode) {
      setFormData(prev => ({ ...prev, referralCode: refCode.toUpperCase() }));
    }
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!agreedToSMS) {
      setError('Please agree to receive SMS verification codes to continue.');
      return;
    }

    setLoading(true);

    try {
      // Format phone number
      let formattedPhone = formData.phoneNumber.replace(/\D/g, '');
      if (!formattedPhone.startsWith('1') && formattedPhone.length === 10) {
        formattedPhone = '1' + formattedPhone;
      }
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = '+' + formattedPhone;
      }

      const response = await signup(
        formattedPhone,
        formData.firstName,
        formData.lastName,
        formData.email || undefined
      );

      if (response.success) {
        navigate('/verify', {
          state: {
            phoneNumber: formattedPhone,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            referralCode: formData.referralCode || undefined,
            isLogin: false
          }
        });
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
      {/* Header with Logo */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="max-w-md mx-auto flex items-center justify-center">
          <img 
            src="/buggybusters_1.jpg" 
            alt="BuggyBusters" 
            className="h-14 w-auto"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          
          {/* Welcome Section */}
          <div className="text-center mb-8">
            <div className="w-48 h-48 mx-auto mb-4">
              <img 
                src="/illustrations/Shopping-rafiki.svg" 
                alt="Join InsiderPass" 
                className="w-full h-full"
              />
            </div>
            <h1 className="text-2xl font-bold text-buggy-dark mb-2">Join InsiderPass</h1>
            <p className="text-buggy-gray">
              Sign up to unlock exclusive BuggyBusters rewards
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Fields - Two Column */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-semibold text-buggy-dark mb-2">
                  First Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User size={18} strokeWidth={2} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    placeholder="First"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-buggy-blue focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-semibold text-buggy-dark mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  placeholder="Last"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-buggy-blue focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-semibold text-buggy-dark mb-2">
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Phone size={18} strokeWidth={2} className="text-gray-400" />
                </div>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  placeholder="+1 555 555 5555"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-buggy-blue focus:border-transparent"
                  required
                />
              </div>
              <p className="text-xs text-buggy-gray mt-2">
                Include country code (e.g., +1 for US)
              </p>
            </div>

            {/* Email (Optional) */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-buggy-dark mb-2">
                Email <span className="text-buggy-gray font-normal">(optional)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail size={18} strokeWidth={2} className="text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-buggy-blue focus:border-transparent"
                />
              </div>
            </div>

            {/* Referral Code (Optional) - Only show if referral program is enabled */}
            {(appConfig?.showReferralProgram !== false) && (
              <div>
                <label htmlFor="referralCode" className="block text-sm font-semibold text-buggy-dark mb-2">
                  Referral Code <span className="text-buggy-gray font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Gift size={18} strokeWidth={2} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="referralCode"
                    name="referralCode"
                    placeholder="Enter friend's code"
                    value={formData.referralCode}
                    onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toUpperCase() })}
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-buggy-blue focus:border-transparent font-mono uppercase"
                    maxLength={8}
                  />
                </div>
                <p className="text-xs text-buggy-gray mt-2">
                  Have a friend's referral code? Enter it to help them earn bonus points!
                </p>
              </div>
            )}

            {/* SMS Consent Checkbox */}
            <div 
              className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setAgreedToSMS(!agreedToSMS)}
            >
              <div className="flex-shrink-0 mt-0.5">
                {agreedToSMS ? (
                  <CheckSquare size={22} strokeWidth={2} className="text-buggy-blue" />
                ) : (
                  <Square size={22} strokeWidth={2} className="text-gray-400" />
                )}
              </div>
              <p className="text-xs text-buggy-gray leading-relaxed">
                I agree to receive SMS messages from InsiderPass including verification codes
                and rewards updates. Message frequency varies. Message and data rates may apply.
                Reply STOP to opt out. View our{' '}
                <Link to="/privacy" className="text-buggy-blue hover:underline">
                  Privacy Policy
                </Link>{' '}
                and{' '}
                <Link to="/terms" className="text-buggy-blue hover:underline">
                  Terms of Service
                </Link>.
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !agreedToSMS}
              className="w-full bg-buggy-blue text-white font-semibold py-4 px-6 rounded-lg hover:bg-buggy-blue-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
            >
              {loading ? 'Creating Account...' : (
                <>
                  Create Account
                  <ArrowRight size={20} strokeWidth={2} />
                </>
              )}
            </button>
          </form>

          {/* Trust Badge */}
          <div className="mt-6 flex items-center justify-center gap-2 text-buggy-gray">
            <Shield size={16} strokeWidth={2} />
            <p className="text-xs">Secure signup for BuggyBusters customers</p>
          </div>

          {/* Divider */}
          <div className="my-8 border-t border-gray-200"></div>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-sm text-buggy-gray mb-2">Already have an account?</p>
            <button
              onClick={() => navigate('/login')}
              className="text-buggy-blue hover:text-buggy-blue-light font-semibold text-base"
            >
              Log in to InsiderPass
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
